import { Box, useBoolean } from '@stenajs-webui/core';
import { type ButtonSize, FlatButton, stenaInfoCircle } from '@stenajs-webui/elements';
import { type FC, useCallback } from 'react';
import type { CalculatePrice_calculatePrice_articlesResults } from '../../common/pricing/graphql/gql/CalculatePrice';
import { type TrackerCategory, trackEvent } from '../../common/tracking/trackerService';
import type { GQLPrice } from '../../use-cases/payment/hooks/usePaymentStatus';
import { formatGQLPrice } from '../../use-cases/payment/utils/paymentUtil';
import { FreightPortalControlledPopover } from '../popover/FreightPortalControlledPopover';
import type { Placement } from '../popover/FreightPortalPopover';
import { CalculatePriceResult } from './CalculatePriceResult';

export interface CalculatePricePopoverProps {
  amountInRouteCurrency: GQLPrice;
  articlesResults: CalculatePrice_calculatePrice_articlesResults[];
  buttonSize?: ButtonSize;
  placement?: Placement;
  trackerCategory: TrackerCategory;
}

export const CalculatePricePopover: FC<CalculatePricePopoverProps> = ({
  articlesResults,
  amountInRouteCurrency,
  trackerCategory,
  placement,
  buttonSize,
}) => {
  const [visible, open, close] = useBoolean(false);

  const onOpen = useCallback(() => {
    open();
    trackEvent({ category: trackerCategory, action: 'Show articles' });
  }, [open, trackerCategory]);

  const inconclusivePrice = articlesResults.some(r => r.warnings.length > 0);

  return (
    <FreightPortalControlledPopover
      renderTrigger={props => (
        <FlatButton
          size={buttonSize}
          leftIcon={inconclusivePrice ? stenaInfoCircle : undefined}
          title={inconclusivePrice ? 'Inconclusive price' : undefined}
          onClick={onOpen}
          label={formatGQLPrice(amountInRouteCurrency)}
          {...props}
        />
      )}
      onRequestClose={close}
      open={visible}
      placement={placement}
    >
      <Box
        maxWidth={800}
        maxHeight={'75vh'}
        overflowY={'auto'}
        borderRadius={'var(--swui-border-radius)'}
      >
        <CalculatePriceResult
          onClose={close}
          amountInRouteCurrency={amountInRouteCurrency}
          articlesResults={articlesResults}
        />
      </Box>
    </FreightPortalControlledPopover>
  );
};
